// Contracts of the Power admin receiver journey (/power/admin/opin/receiver/*): the operator picks
// a transmitter, asks a holder's consent, follows its connection and reads what it received.
// Power calls the insurance receiver with its built-in operator client, validates each answer
// against the receiver's own contract and projects it onto these schemas; the admin SPA depends on
// this contract only, never on a backend package. A drift test in services/power projects every
// receiver vocabulary value through them.
//
// Personal data: a holder's CPF or CNPJ travels only in a request body (create, portfolio) and
// never comes back; no answer below carries a document.
import { Schema } from 'effect';
import { OpinReceiverTransmitterAvailability } from './catalog/availability.ts';
import {
  AuthorisationServerId,
  CNPJ_PATTERN,
  FAPI_HEADERS,
  IsoDateTimeUtc,
} from '@finnest/core/effect';
import { HttpUrl } from '../../_shared/formats.ts';
import { OpinReceiverConnectionError } from './connection-error.ts';
import { OpinConnectionId } from '../consent-lifecycle/consent-lifecycle.ts';
import { OpinReceiverSharedResource } from './resources/schema.ts';

/** Where the receiver journey routes live on Power. */
export const OPIN_RECEIVER_PATH = '/power/admin/opin/receiver';

/** The admin route the transmitter returns the operator's browser to. */
export const OPIN_RECEIVER_RETURN_ROUTE = '/receptora/retorno';

/** The rows of one answered page. */
export const OPIN_RECEIVER_PAGE_SIZE = 25;

// The receiver's bounds: a client id and an idempotency key, and a transmitter name fragment.
const CLIENT_ID_MAX = 255;
const IDEMPOTENCY_KEY_MAX = 255;
const SEARCH_MAX = 100;

const Count = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
const PageNumber = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1));
const Text = Schema.NonEmptyString;
const Instant = IsoDateTimeUtc;

/** The OAuth client id of an application that owns connections. */
export const OpinReceiverClientId = Schema.NonEmptyString.check(Schema.isMaxLength(CLIENT_ID_MAX));
export type OpinReceiverClientId = typeof OpinReceiverClientId.Type;

/** The receiver's insurance types (receiver OPIN_INSURANCE_TYPES). */
export const OPIN_RECEIVER_INSURANCE_TYPES = [
  'patrimonial',
  'auto',
  'person',
  'housing',
  'life_pension',
  'pension_plan',
  'financial_risk',
  'responsibility',
  'rural',
  'transport',
  'acceptance_and_branches_abroad',
  'capitalization_title',
  'financial_assistance',
  'customers',
] as const;
export const OpinReceiverInsuranceType = Schema.Literals(OPIN_RECEIVER_INSURANCE_TYPES);
export type OpinReceiverInsuranceType = typeof OpinReceiverInsuranceType.Type;

/** A connection's status (receiver CONNECTION_STATUSES). */
export const OPIN_RECEIVER_CONNECTION_STATUSES = [
  'pending',
  'awaiting_auth',
  'syncing',
  'active',
  'error',
  'revoked',
  'revocation_pending',
  'expired',
] as const;
export const OpinReceiverConnectionStatus = Schema.Literals(OPIN_RECEIVER_CONNECTION_STATUSES);
export type OpinReceiverConnectionStatus = typeof OpinReceiverConnectionStatus.Type;

/** Why a connection ended (receiver CONNECTION_END_REASONS). */
export const OpinReceiverEndReason = Schema.Literals([
  'max_date_reached',
  'authorization_timeout',
  'authorization_failed',
  'customer_revoked',
  'customer_revoked_at_transmitter',
  'transmitter_security',
  'participant_security',
]);
export type OpinReceiverEndReason = typeof OpinReceiverEndReason.Type;

/** Whether the holder is a customer of the participant (PCM `consent-user`). */
export const OpinReceiverConsentUser = Schema.Literals(['user', 'non-user']);
export type OpinReceiverConsentUser = typeof OpinReceiverConsentUser.Type;

/** The longest consent validity the regulation allows, in months (Res. CNSP 415, art. 11). */
export const OPIN_RECEIVER_VALIDITY_MONTHS_MAX = 12;
/** The longest purpose text the receiver stores. */
export const OPIN_RECEIVER_PURPOSE_MAX = 500;

const Meta = Schema.Struct({ page: PageNumber, totalRecords: Count, totalPages: Count });

/** One page of `item`. */
export const pageOf = <S extends Schema.Top>(
  item: S,
): Schema.Struct<{ readonly data: Schema.$Array<S>; readonly meta: typeof Meta }> =>
  Schema.Struct({ data: Schema.Array(item), meta: Meta });

/**
 * A transmitter of the official Directory, with the organisation that registers it, the host of
 * its issuer and of its OIDC discovery document, and whether the operator can ask a consent from it.
 */
export const OpinReceiverTransmitter = Schema.Struct({
  id: AuthorisationServerId,
  name: Text,
  logoUrl: Schema.NullOr(HttpUrl),
  supportedTypes: Schema.Array(OpinReceiverInsuranceType),
  organisation: Schema.Struct({ name: Text, registrationNumber: Text }),
  issuerHost: Text,
  discoveryHost: Text,
  availability: OpinReceiverTransmitterAvailability,
});
export type OpinReceiverTransmitter = typeof OpinReceiverTransmitter.Type;

export const OpinReceiverTransmitterPage = pageOf(OpinReceiverTransmitter);
export type OpinReceiverTransmitterPage = typeof OpinReceiverTransmitterPage.Type;

/** The transmitter search: a fragment of the transmitter's or its organisation's name, and the page. */
export const OpinReceiverTransmitterQuery = Schema.Struct({
  q: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(SEARCH_MAX))),
  page: Schema.optionalKey(
    Schema.NumberFromString.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
  ),
});

/** The consent terms a connection was asked with. */
export const OpinReceiverConsentTerms = Schema.Struct({
  purpose: Text,
  validityMonths: Schema.Int.check(
    Schema.isBetween({ minimum: 1, maximum: OPIN_RECEIVER_VALIDITY_MONTHS_MAX }),
  ),
  consentUser: OpinReceiverConsentUser,
});
export type OpinReceiverConsentTerms = typeof OpinReceiverConsentTerms.Type;

/** The last sync stored some product families and not these: when it ended, and why each failed. */
export const OpinReceiverSyncIncomplete = Schema.Struct({
  at: Instant,
  failedFamilies: Schema.Array(
    Schema.Struct({ family: OpinReceiverInsuranceType, reason: Text }),
  ).check(Schema.isMinLength(1)),
});
export type OpinReceiverSyncIncomplete = typeof OpinReceiverSyncIncomplete.Type;

/** A connection as the operator follows it; it never carries a holder document. */
export const OpinReceiverConnection = Schema.Struct({
  id: OpinConnectionId,
  clientId: OpinReceiverClientId,
  authorisationServerId: AuthorisationServerId,
  authorisationServerName: Text,
  status: OpinReceiverConnectionStatus,
  supportedTypes: Schema.Array(OpinReceiverInsuranceType),
  grantedScopes: Schema.Array(Text),
  consentTerms: OpinReceiverConsentTerms,
  consentExpiresAt: Schema.NullOr(Instant),
  lastSyncAt: Schema.NullOr(Instant),
  error: Schema.NullOr(OpinReceiverConnectionError),
  syncIncomplete: Schema.NullOr(OpinReceiverSyncIncomplete),
  endReason: Schema.NullOr(OpinReceiverEndReason),
  createdAt: Instant,
  updatedAt: Instant,
});
export type OpinReceiverConnection = typeof OpinReceiverConnection.Type;

/** A CPF as the form sends it: 11 digits. Power checks its check digits. Personal data. */
export const OpinReceiverCpf = Schema.String.check(
  Schema.isPattern(/^\d{11}$/u, { expected: '11 digits' }),
);
/** A CNPJ as the form sends it, numeric or alphanumeric. Power checks its check digits. */
export const OpinReceiverCnpj = Schema.String.check(
  Schema.isPattern(CNPJ_PATTERN, { expected: '14 characters' }),
);

/**
 * A new connection: the transmitter, the holder and the consent terms. Power adds where the
 * transmitter returns the browser; the screen's idempotency key travels in `x-idempotency-key`.
 */
export const OpinReceiverConnectionCreate = Schema.Struct({
  authorisationServerId: AuthorisationServerId,
  holderDocument: OpinReceiverCpf,
  businessEntity: Schema.optionalKey(OpinReceiverCnpj),
  requestedTypes: Schema.optionalKey(Schema.Array(OpinReceiverInsuranceType)),
  purpose: Schema.String.check(
    Schema.isMinLength(1),
    Schema.isMaxLength(OPIN_RECEIVER_PURPOSE_MAX),
  ),
  validityMonths: Schema.Int.check(
    Schema.isBetween({ minimum: 1, maximum: OPIN_RECEIVER_VALIDITY_MONTHS_MAX }),
  ),
  consentUser: OpinReceiverConsentUser,
});
export type OpinReceiverConnectionCreate = typeof OpinReceiverConnectionCreate.Type;

/**
 * The `detail` of Power's 409 to a create the screen tells apart: a create under the same
 * idempotency key is still running (`create-in-progress`; a retry under that key resumes it), or
 * the holder already has a live connection with the transmitter (`already-connected`; the operator
 * follows or revokes that connection). Any other refused create created nothing.
 */
export const OpinReceiverCreateConflict = Schema.Literals([
  'create-in-progress',
  'already-connected',
]);
export type OpinReceiverCreateConflict = typeof OpinReceiverCreateConflict.Type;

/**
 * Why Power answers a receiver journey route 503, as the code of its Problem's one error entry: the
 * deployment lacks the journey's configuration, Keycloak gave no token for the built-in client,
 * the insurance receiver failed, the audit ledger could not record the action, or the
 * transmitter serves a TLS certificate the receiver refuses: one not for a TLS server
 * (extendedKeyUsage without serverAuth), one no trusted authority issued, or one naming another
 * host. The Problem's detail is the code's fixed reason; the cause itself stays in Power's logs,
 * under the Problem's trace id.
 */
export const OpinReceiverUnavailableCode = Schema.Literals([
  'receiver_unconfigured',
  'token_unavailable',
  'receiver_upstream_error',
  'audit_unavailable',
  'transmitter_certificate_not_server',
  'transmitter_certificate_untrusted',
  'transmitter_certificate_wrong_host',
]);
export type OpinReceiverUnavailableCode = typeof OpinReceiverUnavailableCode.Type;

/**
 * Why Power answers a create 409 when an earlier DCR registration attempt with the transmitter is
 * still pending and the receiver cannot prove it sent nothing, as the code of its Problem's one
 * error entry: the attempt began before the receiver recorded its HTTP exchanges with the
 * transmitter, its POST got no answer, or the transmitter answered it and it was not completed.
 * The Problem's detail names the attempt and the exact `finnest dcr reconcile` command the
 * operator runs once the transmitter confirms what it holds.
 */
export const OpinReceiverReconciliationCode = Schema.Literals([
  'dcr_attempt_before_trail',
  'dcr_attempt_unanswered',
  'dcr_attempt_answered',
]);
export type OpinReceiverReconciliationCode = typeof OpinReceiverReconciliationCode.Type;

/** The idempotency key the screen generates when the form opens (receiver IdempotencyKey). */
export const OpinReceiverIdempotencyKey = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(IDEMPOTENCY_KEY_MAX),
);

export const OpinReceiverCreateHeaders = Schema.Struct({
  [FAPI_HEADERS.idempotencyKey]: OpinReceiverIdempotencyKey,
});

// Hosts an `http` authorization page may name: a transmitter on the developer's own machine.
const LOOPBACK_HOSTS: ReadonlySet<string> = new Set(['localhost', '127.0.0.1', '[::1]']);

const isWebPage = (url: URL): boolean =>
  url.protocol === 'https:' || (url.protocol === 'http:' && LOOPBACK_HOSTS.has(url.hostname));

/**
 * The transmitter's authorization page the operator's browser is sent to: `https`, or `http` only
 * on a loopback host. A scheme the browser would run instead of load (`javascript:`, `data:`) is
 * refused, whatever the transmitter's metadata named.
 */
export const OpinReceiverAuthorizationUrl = Schema.String.check(
  Schema.makeFilter(
    (value: string) =>
      (URL.canParse(value) && isWebPage(new URL(value))) ||
      'expected an https URL (http only on a loopback host)',
  ),
).annotate({ format: 'uri' });

/** The new connection, and the transmitter's authorization page the browser goes to. */
export const OpinReceiverConnectionCreated = Schema.Struct({
  connection: OpinReceiverConnection,
  redirectUrl: Schema.NullOr(OpinReceiverAuthorizationUrl),
});
export type OpinReceiverConnectionCreated = typeof OpinReceiverConnectionCreated.Type;

/**
 * Why the operator revokes: at the holder's request (any acting role), or for security or fraud
 * (owners and admins only). Reported to the PCM consent funnel as revoked by `user` and by `system`.
 */
export const OPIN_RECEIVER_REVOCATION_REASONS = ['holder_request', 'security'] as const;
export const OpinReceiverRevocationReason = Schema.Literals(OPIN_RECEIVER_REVOCATION_REASONS);
export type OpinReceiverRevocationReason = typeof OpinReceiverRevocationReason.Type;

/** A revocation: always with its reason, never a default. */
export const OpinReceiverRevocation = Schema.Struct({ reason: OpinReceiverRevocationReason });
export type OpinReceiverRevocation = typeof OpinReceiverRevocation.Type;

/** What the session's operator may do: the revocation reasons it may choose (none to read only). */
export const OpinReceiverCapabilities = Schema.Struct({
  revocationReasons: Schema.Array(OpinReceiverRevocationReason),
});
export type OpinReceiverCapabilities = typeof OpinReceiverCapabilities.Type;

/** A sync or revocation the receiver accepted. */
export const OpinReceiverAccepted = Schema.Struct({ ok: Schema.Literal(true) });

/** One holder's connections, by CPF and optional CNPJ. Never echoed back. */
export const OpinReceiverPortfolioLookup = Schema.Struct({
  holderDocument: OpinReceiverCpf,
  businessEntity: Schema.optionalKey(OpinReceiverCnpj),
  clientId: Schema.optionalKey(OpinReceiverClientId),
});
export type OpinReceiverPortfolioLookup = typeof OpinReceiverPortfolioLookup.Type;

/** The records a connection's syncs stored, per product. */
export const OpinReceiverProductCounts = Schema.Struct({
  policyholders: Count,
  policies: Count,
  premiums: Count,
  claims: Count,
});

/** A listed connection and the records its syncs stored. */
export const OpinReceiverListedConnection = Schema.Struct({
  connection: OpinReceiverConnection,
  counts: OpinReceiverProductCounts,
});
export type OpinReceiverListedConnection = typeof OpinReceiverListedConnection.Type;

/** How many connections of each status the list's other filters match, whatever their status. */
export const OpinReceiverStatusCounts = Schema.Struct({
  pending: Count,
  awaiting_auth: Count,
  syncing: Count,
  active: Count,
  error: Count,
  revoked: Count,
  revocation_pending: Count,
  expired: Count,
} satisfies Record<OpinReceiverConnectionStatus, typeof Count>);
export type OpinReceiverStatusCounts = typeof OpinReceiverStatusCounts.Type;

/** One page of the connection list, each with its records, and the filters' per-status counts. */
export const OpinReceiverConnectionPage = Schema.Struct({
  data: Schema.Array(OpinReceiverListedConnection),
  statusCounts: OpinReceiverStatusCounts,
  meta: Meta,
});
export type OpinReceiverConnectionPage = typeof OpinReceiverConnectionPage.Type;

/** One connection, the records its syncs stored, and the resources its consent shares. */
export const OpinReceiverConnectionDetail = Schema.Struct({
  connection: OpinReceiverConnection,
  counts: OpinReceiverProductCounts,
  resources: Schema.Array(OpinReceiverSharedResource),
});
export type OpinReceiverConnectionDetail = typeof OpinReceiverConnectionDetail.Type;

export const OpinReceiverPortfolioEntry = Schema.Struct({
  connection: Schema.Struct({
    id: OpinConnectionId,
    clientId: OpinReceiverClientId,
    authorisationServerId: AuthorisationServerId,
    authorisationServerName: Text,
    status: OpinReceiverConnectionStatus,
    lastSyncAt: Schema.NullOr(Instant),
    consentExpiresAt: Schema.NullOr(Instant),
  }),
  counts: OpinReceiverProductCounts,
});
export type OpinReceiverPortfolioEntry = typeof OpinReceiverPortfolioEntry.Type;

export const OpinReceiverPortfolio = Schema.Struct({
  data: Schema.Array(OpinReceiverPortfolioEntry),
});
export type OpinReceiverPortfolio = typeof OpinReceiverPortfolio.Type;
